import { defineElement, FC, onDidLoad, onDidUnload, useElements, useListen } from '@atomify/hooks';
import { inviewObserver } from '@source/utilities/inview';

const STATUS_BAR_INDICATOR_HOOK = 'js-hook-status-bar-indicator';
const STATUS_BAR_ITEM_HOOK = 'js-hook-status-bar-item';

const STATUS_BAR_INDICATOR_UNCHECKED_CLASS = 'status-bar__indicator--unchecked';
const STATUS_BAR_INDICATOR_CHECKING_CLASS = 'status-bar__indicator--checking';
const STATUS_BAR_INDICATOR_CHECKED_CLASS = 'status-bar__indicator--checked';

type IndicatorState = 'unchecked' | 'checking' | 'checked';

const StatusBar: FC = () => {
    const items = useElements<HTMLElement[]>(`[${STATUS_BAR_ITEM_HOOK}]`);
    const indicators = useElements<HTMLElement[]>(`[${STATUS_BAR_INDICATOR_HOOK}]`);

    let windowHeight = window.innerHeight;
    useListen(window, 'resize', () => {
        windowHeight = window.innerHeight;
    });

    onDidLoad(() => {
        Array.from(items.current).forEach((item) => {
            if (!item) return;
            const itemId = item.getAttribute(STATUS_BAR_ITEM_HOOK) || '';
            inviewObserver.observe(item, (inView, intersection) =>
                itemVisibilityChanged(itemId, inView, intersection),
            );
        });

        Array.from(indicators.current).forEach((indicator) => {
            if (!indicator) return;
            const indicatorId = indicator.getAttribute(STATUS_BAR_INDICATOR_HOOK) || '';
            indicator.addEventListener('click', () => onIndicatorClick(indicatorId));
        });
    });

    onDidUnload(() => {
        Array.from(items.current).forEach((item) => inviewObserver.unobserve(item));
    });

    const itemVisibilityChanged = (
        itemId: string,
        inView: boolean,
        intersection: IntersectionObserverEntry,
    ) => {
        if (inView) {
            if (intersection.boundingClientRect.bottom <= windowHeight) {
                setIndicatorState(itemId, 'checked');
            } else {
                setIndicatorState(itemId, 'checking');
            }
        } else {
            if (intersection.boundingClientRect.top >= windowHeight) {
                setIndicatorState(itemId, 'unchecked');
            }
        }
    };

    const onIndicatorClick = (indicatorId: string) => {
        Array.from(items.current).forEach((item) => {
            if (!item) return;
            const itemId = item.getAttribute(STATUS_BAR_ITEM_HOOK);
            if (itemId === indicatorId) {
                const safeOffset = 200;
                const y = item.getBoundingClientRect().top + window.pageYOffset - safeOffset;
                window.scrollTo({ top: y, behavior: 'smooth' });
            }
        });
    };

    const setIndicatorState = (indicatorId: string, state: IndicatorState) => {
        Array.from(indicators.current).forEach((indicator) => {
            if (indicator && indicator.getAttribute(STATUS_BAR_INDICATOR_HOOK) === indicatorId) {
                switch (state) {
                    case 'unchecked':
                        indicator.classList.remove(STATUS_BAR_INDICATOR_CHECKING_CLASS);
                        indicator.classList.remove(STATUS_BAR_INDICATOR_CHECKED_CLASS);
                        indicator.classList.add(STATUS_BAR_INDICATOR_UNCHECKED_CLASS);
                        break;
                    case 'checking':
                        indicator.classList.remove(STATUS_BAR_INDICATOR_UNCHECKED_CLASS);
                        indicator.classList.remove(STATUS_BAR_INDICATOR_CHECKED_CLASS);
                        indicator.classList.add(STATUS_BAR_INDICATOR_CHECKING_CLASS);
                        break;
                    case 'checked':
                        indicator.classList.remove(STATUS_BAR_INDICATOR_UNCHECKED_CLASS);
                        indicator.classList.remove(STATUS_BAR_INDICATOR_CHECKING_CLASS);
                        indicator.classList.add(STATUS_BAR_INDICATOR_CHECKED_CLASS);
                        break;
                }
            }
        });
    };
};

defineElement('status-bar', StatusBar);
